import type { NodeUsageSeries, UsageTimeframe } from '@/api/types'
import { sizeUnit, type SizeUnit } from '@/ui/format'

// 노드 사용량 시계열을 그래프 행으로 옮기는 순수 함수. 닿지 않던 구간은 null 그대로다 — 0 으로 메우면 한가했던 시간이 된다.

export type UsageRow = {
  time: string
  label: string
  /** CPU 사용률 %, 소수 한 자리. */
  cpu: number | null
  /** RAM 사용량 GB, 소수 한 자리. */
  ram: number | null
  ramTotal: number | null
  netIn: number | null
  netOut: number | null
  /** GPU id → 사용률 %. 보고되지 않은 GPU 는 키가 없다. */
  gpus: Record<string, number | null>
}

export const TIMEFRAMES: readonly { value: UsageTimeframe; word: string }[] = [
  { value: 'hour', word: '1시간' },
  { value: 'day', word: '하루' },
  { value: 'week', word: '한 주' },
]

const tenth = (n: number) => Math.round(n * 10) / 10

export function usageRows(series: NodeUsageSeries | null | undefined): UsageRow[] {
  if (!series || !Array.isArray(series.points)) return []
  return series.points.map((p) => ({
    time: p.time,
    label: timeLabel(p.time, series.timeframe),
    cpu: p.cpu === null || p.cpu === undefined ? null : tenth(p.cpu * 100),
    ram: p.memory_used_mb === null || p.memory_used_mb === undefined ? null : tenth(p.memory_used_mb / 1024),
    ramTotal: p.memory_total_mb === null || p.memory_total_mb === undefined ? null : tenth(p.memory_total_mb / 1024),
    netIn: p.net_in ?? null,
    netOut: p.net_out ?? null,
    gpus: Object.fromEntries((p.gpus ?? []).map((g) => [g.gpu_id, g.util === null || g.util === undefined ? null : tenth(g.util * 100)])),
  }))
}

/** 한 그래프에 겹쳐 그릴 GPU. label 은 화면 낱말(모델 · 슬롯). */
export type GpuLine = { id: string; label: string }

/**
 * 켠 GPU 들의 사용률을 recharts 가 읽는 평평한 행으로. 키는 g0, g1… (dataKey 와 ChartConfig 키는 같아야 하고
 * uuid 는 CSS 변수 이름에 들어갈 수 없다). 값이 없는 GPU 는 그 점에서 null 이라 선이 끊긴다.
 */
export function gpuChartRows(rows: UsageRow[], lines: GpuLine[]): Record<string, string | number | null>[] {
  return rows.map((r) => {
    const out: Record<string, string | number | null> = { label: r.label, time: r.time }
    lines.forEach((g, i) => {
      out[`g${i}`] = r.gpus[g.id] ?? null
    })
    return out
  })
}

/** 슬롯 표기: `0000:82:00.0` → `82:00.0`. */
export function gpuSlot(pciAddr: string | null | undefined): string {
  if (!pciAddr) return ''
  const i = pciAddr.indexOf(':')
  return i >= 0 && pciAddr.length > i + 1 && pciAddr.slice(0, i).length === 4 ? pciAddr.slice(i + 1) : pciAddr
}

/** 축 눈금. 1시간은 시:분, 그보다 길면 월-일 시:분. */
export function timeLabel(iso: string, timeframe: UsageTimeframe): string {
  const t = Date.parse(iso)
  if (!Number.isFinite(t)) return ''
  const d = new Date(t)
  const p = (n: number) => String(n).padStart(2, '0')
  const hm = `${p(d.getHours())}:${p(d.getMinutes())}`
  return timeframe === 'hour' ? hm : `${p(d.getMonth() + 1)}-${p(d.getDate())} ${hm}`
}

/** RAM 축의 위 끝 — 측정된 총량의 최대. 총량을 모르면 undefined(자동). */
export function ramCeiling(rows: UsageRow[]): number | undefined {
  const totals = rows.map((r) => r.ramTotal).filter((n): n is number => n !== null)
  // 축의 맨 위 눈금이 되는 값이라 정수로 올린다 — 125.5 는 눈금 폭을 넘어 `25.5` 로 잘려 보였다(#477).
  return totals.length === 0 ? undefined : Math.ceil(Math.max(...totals))
}

/**
 * RAM 축의 단위와 눈금. 값은 GiB 그대로 두고 글자만 위 끝(`ramCeiling`)이 정한 단위로 적는다(`ui/format.ts` 의 사다리) —
 * 머리의 `쓴 양 / 총량` 과 같은 단위다. TB 의 눈금은 0.5TB 의 배수에 두어 글자가 눈금의 자리와 맞는다(자동 눈금은 500·1000 에 선다).
 */
export function ramAxis(ceiling: number | undefined): { unit: SizeUnit; ticks: number[] | undefined } {
  const unit = sizeUnit(ceiling)
  if (unit === 'GB' || ceiling === undefined) return { unit, ticks: undefined }
  const step = 512 * Math.max(1, Math.ceil(ceiling / 512 / 4))
  return { unit, ticks: Array.from({ length: Math.floor(ceiling / step) + 1 }, (_, i) => i * step) }
}

/** y축 폭(px). 가장 긴 눈금 글자(위 끝의 글자)의 길이로 정한다 — 두 자리 기준의 고정 폭은 세 자리 눈금을 자른다(#477). */
export function axisWidth(top: string | undefined): number {
  return Math.max(30, 12 + 7 * (top ?? '').length)
}

/** 값이 있는 마지막 행. 그래프 머리의 현재값이 된다. */
export function lastMeasured(rows: UsageRow[]): UsageRow | null {
  for (let i = rows.length - 1; i >= 0; i--) {
    const r = rows[i]!
    if (r.cpu !== null || r.ram !== null) return r
  }
  return null
}

/** 바이트/초를 MB/s 로. */
export function mbps(bytesPerSecond: number | null | undefined): string {
  if (bytesPerSecond === null || bytesPerSecond === undefined) return '—'
  return `${tenth(bytesPerSecond / 1048576)}MB/s`
}
